import { useCurrentStateAndParams } from "@uirouter/react";

import type { SubtitleFilter } from "@similarweb/pro-logic-components";

import type { WebSource } from "app/components/filters-bar/websource-filter/WebSourceFilter.types";
import { translate } from "app/filters/i18nFilter";
import CountryService from "app/services/CountryService";
import { default as DurationService } from "app/services/DurationService";

export type SubtitleFilters = SubtitleFilter[];
export type FilterNames = SubtitleFilter["filter"];

type ExtractSubtitleFilterByName<T extends FilterNames> = Extract<SubtitleFilter, { filter: T }>;

type MapFiltersToTypes<T extends readonly FilterNames[]> = {
    [K in keyof T]: T[K] extends FilterNames ? ExtractSubtitleFilterByName<T[K]> : never;
};

export const useSubtitleFilters = <T extends readonly FilterNames[]>(...filters: T): MapFiltersToTypes<T> => {
    const filtersClean = filters?.filter(Boolean) ?? [];
    const { params } = useCurrentStateAndParams() as {
        params: { country?: number; duration?: string; comparedDuration?: string; webSource?: string };
    };
    const { country, duration, comparedDuration, webSource } = params;
    const countryName = CountryService.getCountryById(country)?.text;
    // @ts-ignore FIXME: strict migration - DurationService.getDurationData expects string but duration may be undefined
    const { from, to, compareFrom, compareTo } = DurationService.getDurationData(duration, comparedDuration).forAPI;
    const webSourceDisplayName = `websources.${webSource?.toLowerCase()}`;

    return filtersClean.map((filter: FilterNames) => {
        switch (filter) {
            case "country":
                return {
                    filter,
                    countryCode: country,
                    value: countryName,
                };
            case "date":
                return {
                    filter,
                    value: {
                        from,
                        to,
                        compareFrom,
                        compareTo,
                    },
                };
            case "webSource":
                return {
                    filter,
                    value: webSource as WebSource,
                    displayName: translate(webSourceDisplayName),
                };
        }
    }) as MapFiltersToTypes<T>;
};

type Bullet = React.ReactNode;

type DateFilterWithColoredBullets = ExtractSubtitleFilterByName<"date"> & {
    value: ExtractSubtitleFilterByName<"date">["value"] & {
        coloredBullets: {
            dateBullet: Bullet;
            compareDateBullet: Bullet;
        };
    };
};

type SubtitleFilterWithCustomColors = Exclude<SubtitleFilter, { filter: "date" }> | DateFilterWithColoredBullets;

export const subtitleFiltersWithCustomColors = (
    subtitleFilters: SubtitleFilters = [],
    dateBullet: Bullet,
    compareDateBullet: Bullet,
): SubtitleFilterWithCustomColors[] => {
    return subtitleFilters.map((item): SubtitleFilterWithCustomColors => {
        if (item.filter !== "date") {
            return item;
        }

        return {
            ...item,
            value: {
                ...item.value,
                coloredBullets: {
                    dateBullet: dateBullet,
                    compareDateBullet: compareDateBullet,
                },
            },
        };
    });
};
